import { DialogComponent } from '@theme/dialog';

/**
 * Interactive Bammies size guide — weight/age slider, product cards, and chart.
 *
 * Chart X-axis is a linear weight scale (3–20 kg) with age labels overlaid.
 * Bars highlight when the current weight falls within their range; the vertical
 * indicator tracks the slider with a short CSS transition.
 *
 * Copy is provided by Liquid via [data-size-guide-i18n] (locale keys under
 * sections.size_guide). Placeholders use Shopify style: {{ value }}, {{ count }}, etc.
 *
 * Extends HTMLElement (not theme Component) so dialog-component can own
 * closeButton / on:click handlers nested in the same dialog tree.
 */

const WEIGHT_MIN = 3;
const WEIGHT_MAX = 20;
const DEFAULT_KG = 8.5;

/**
 * Age markers aligned to the Figma chart axis.
 * `axis` is the horizontal position (0–100) matching the chart age labels,
 * which spreads the first year more than a linear months scale.
 * Labels come from i18n via `labelKey`.
 */
const AGE_MARKERS = [
  { months: 0, labelKey: 'age_marker_birth', kg: 3, axis: 0 },
  { months: 2, labelKey: 'age_marker_2_mo', kg: 4.87, axis: 11 },
  { months: 5, labelKey: 'age_marker_5_mo', kg: 6.93, axis: 23.1 },
  { months: 10, labelKey: 'age_marker_10_mo', kg: 9.03, axis: 35.5 },
  { months: 24, labelKey: 'age_marker_2_yr', kg: 12.48, axis: 55.8 },
  { months: 48, labelKey: 'age_marker_4_yr', kg: 15.82, axis: 75.4 },
  { months: 72, labelKey: 'age_marker_6_yr', kg: 20, axis: 100 },
];

const AGE_MIN_MONTHS = AGE_MARKERS[0].months;
const AGE_MAX_MONTHS = AGE_MARKERS[AGE_MARKERS.length - 1].months;

const I18N_DEFAULTS = {
  kg_value: '{{ value }} kg',
  kg_paren: '({{ value }} kg)',
  weight_range: '{{ min }}-{{ max }} kg',
  card_meta: '{{ min }}–{{ max }} kg · {{ pack }} {{ pack_label }} / mo',
  card_meta_weight_only: '{{ min }}–{{ max }} kg',
  card_cta: 'Take a look',
  age_months: '{{ count }} mo',
  age_years: '{{ count }} yr',
  age_range_months: '({{ lo }}-{{ hi }} mo)',
  age_approx_years: '(~{{ count }} yr)',
  newborn: 'newborn',
  newborn_paren: '(newborn)',
  aria_child_weight: 'Child weight',
  aria_child_age: 'Child age',
  aria_valuetext_kg: '{{ value }} kilograms',
  age_marker_birth: 'birth',
  age_marker_2_mo: '2 mo',
  age_marker_5_mo: '5 mo',
  age_marker_10_mo: '10 mo',
  age_marker_2_yr: '2 yr',
  age_marker_4_yr: '4 yr',
  age_marker_6_yr: '6 yr',
};

/**
 * @typedef {object} SizeProduct
 * @property {string} id
 * @property {string} name
 * @property {'diaper' | 'pullup'} type
 * @property {number} min
 * @property {number} max
 * @property {number | null} pack
 * @property {string} packLabel
 * @property {string} image
 * @property {string} url
 * @property {string} cardTitle
 */

/**
 * @param {string} template
 * @param {Record<string, string | number>} vars
 */
function interpolate(template, vars = {}) {
  return String(template).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) =>
    vars[key] !== undefined && vars[key] !== null ? String(vars[key]) : `{{ ${key} }}`
  );
}

/**
 * @param {number} kg
 * @param {number} min
 * @param {number} max
 */
function weightPercent(kg, min = WEIGHT_MIN, max = WEIGHT_MAX) {
  return Math.min(100, Math.max(0, ((kg - min) / (max - min)) * 100));
}

/**
 * Map months → chart/slider axis percent using the non-linear age scale.
 * @param {number} months
 */
function monthsToAxis(months) {
  const clamped = Math.min(AGE_MAX_MONTHS, Math.max(AGE_MIN_MONTHS, months));
  for (let i = 0; i < AGE_MARKERS.length - 1; i++) {
    const a = AGE_MARKERS[i];
    const b = AGE_MARKERS[i + 1];
    if (clamped >= a.months && clamped <= b.months) {
      const t = (clamped - a.months) / (b.months - a.months || 1);
      return a.axis + t * (b.axis - a.axis);
    }
  }
  return 100;
}

/**
 * Map chart/slider axis percent → months.
 * @param {number} axisPercent 0–100
 */
function axisToMonths(axisPercent) {
  const clamped = Math.min(100, Math.max(0, axisPercent));
  for (let i = 0; i < AGE_MARKERS.length - 1; i++) {
    const a = AGE_MARKERS[i];
    const b = AGE_MARKERS[i + 1];
    if (clamped >= a.axis && clamped <= b.axis) {
      const t = (clamped - a.axis) / (b.axis - a.axis || 1);
      return a.months + t * (b.months - a.months);
    }
  }
  return AGE_MAX_MONTHS;
}

/**
 * @param {number} months
 */
function monthsToKg(months) {
  const clamped = Math.min(AGE_MAX_MONTHS, Math.max(AGE_MIN_MONTHS, months));
  for (let i = 0; i < AGE_MARKERS.length - 1; i++) {
    const a = AGE_MARKERS[i];
    const b = AGE_MARKERS[i + 1];
    if (clamped >= a.months && clamped <= b.months) {
      const t = (clamped - a.months) / (b.months - a.months || 1);
      return a.kg + t * (b.kg - a.kg);
    }
  }
  return WEIGHT_MAX;
}

/**
 * @param {number} kg
 */
function kgToMonths(kg) {
  const clamped = Math.min(WEIGHT_MAX, Math.max(WEIGHT_MIN, kg));
  for (let i = 0; i < AGE_MARKERS.length - 1; i++) {
    const a = AGE_MARKERS[i];
    const b = AGE_MARKERS[i + 1];
    if (clamped >= a.kg && clamped <= b.kg) {
      const t = (clamped - a.kg) / (b.kg - a.kg || 1);
      return a.months + t * (b.months - a.months);
    }
  }
  return AGE_MAX_MONTHS;
}

/**
 * @param {string} value
 */
function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

/**
 * @param {number} kg
 */
function formatKg(kg) {
  const rounded = Math.round(kg * 2) / 2;
  return Number.isInteger(rounded) ? `${rounded}` : rounded.toFixed(1);
}

class SizeGuideApp extends HTMLElement {
  /** @type {'weight' | 'age'} */
  #mode = 'weight';
  /** @type {number} */
  #kg = DEFAULT_KG;
  /** @type {SizeProduct[]} */
  #products = [];
  /** @type {Record<string, string>} */
  #i18n = { ...I18N_DEFAULTS };
  /** @type {boolean} */
  #dragging = false;

  connectedCallback() {
    this.#products = this.#parseProducts();
    this.#i18n = { ...I18N_DEFAULTS, ...this.#parseI18n() };
    this.#kg = DEFAULT_KG;
    this.#bindStatic();
    this.#render();
  }

  /**
   * @param {string} key
   * @param {Record<string, string | number>} [vars]
   */
  #t(key, vars) {
    const template = this.#i18n[key] ?? I18N_DEFAULTS[key] ?? key;
    return vars ? interpolate(template, vars) : template;
  }

  #parseProducts() {
    const script = this.querySelector('script[type="application/json"][data-size-guide-products]');
    if (script?.textContent) {
      try {
        return /** @type {SizeProduct[]} */ (JSON.parse(script.textContent));
      } catch {
        /* fall through */
      }
    }
    return [];
  }

  #parseI18n() {
    const script = this.querySelector('script[type="application/json"][data-size-guide-i18n]');
    if (script?.textContent) {
      try {
        return /** @type {Record<string, string>} */ (JSON.parse(script.textContent));
      } catch {
        /* fall through */
      }
    }
    return {};
  }

  #bindStatic() {
    this.querySelectorAll('[data-mode]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const mode = /** @type {'weight' | 'age'} */ (btn.getAttribute('data-mode'));
        if (mode === this.#mode) return;
        this.#mode = mode;
        this.#render();
      });
    });

    const track = this.querySelector('[data-slider-track]');
    if (track instanceof HTMLElement) {
      track.addEventListener('pointerdown', this.#onPointerDown);
      track.addEventListener('keydown', this.#onKeyDown);
    }

    const scroller = this.querySelector('[data-cards-scroller]');
    const prev = this.querySelector('[data-cards-prev]');
    const next = this.querySelector('[data-cards-next]');
    if (scroller && prev && next) {
      prev.addEventListener('click', () => this.#scrollCards(-1));
      next.addEventListener('click', () => this.#scrollCards(1));
      scroller.addEventListener('scroll', () => this.#updateCardsProgress(), { passive: true });
    }

    const labels = this.querySelector('[data-slider-labels]');
    if (labels instanceof HTMLElement && typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => {
        if (this.#mode === 'age') this.#cullOverlappingAgeMarks(labels);
      });
      ro.observe(labels);
    }
  }

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    const track = /** @type {HTMLElement} */ (event.currentTarget);
    this.#dragging = true;
    track.setPointerCapture(event.pointerId);
    this.#updateFromPointer(event, track);
    track.addEventListener('pointermove', this.#onPointerMove);
    track.addEventListener('pointerup', this.#onPointerUp);
    track.addEventListener('pointercancel', this.#onPointerUp);
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (!this.#dragging) return;
    const track = /** @type {HTMLElement} */ (event.currentTarget);
    this.#updateFromPointer(event, track);
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    this.#dragging = false;
    const track = /** @type {HTMLElement} */ (event.currentTarget);
    track.releasePointerCapture?.(event.pointerId);
    track.removeEventListener('pointermove', this.#onPointerMove);
    track.removeEventListener('pointerup', this.#onPointerUp);
    track.removeEventListener('pointercancel', this.#onPointerUp);
  };

  /**
   * @param {PointerEvent} event
   * @param {HTMLElement} track
   */
  #updateFromPointer(event, track) {
    const rect = track.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    this.#applyRatio(ratio);
  }

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    const step = this.#mode === 'weight' ? 0.5 / (WEIGHT_MAX - WEIGHT_MIN) : 0.02;
    let delta = 0;
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') delta = step;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') delta = -step;
    if (event.key === 'Home') {
      this.#applyRatio(0);
      event.preventDefault();
      return;
    }
    if (event.key === 'End') {
      this.#applyRatio(1);
      event.preventDefault();
      return;
    }
    if (!delta) return;
    event.preventDefault();
    const percent =
      this.#mode === 'weight' ? weightPercent(this.#kg) / 100 : monthsToAxis(kgToMonths(this.#kg)) / 100;
    this.#applyRatio(percent + delta);
  };

  /** @param {number} ratio */
  #applyRatio(ratio) {
    const clamped = Math.min(1, Math.max(0, ratio));
    if (this.#mode === 'weight') {
      const raw = WEIGHT_MIN + clamped * (WEIGHT_MAX - WEIGHT_MIN);
      this.#kg = Math.round(raw * 2) / 2;
    } else {
      const months = axisToMonths(clamped * 100);
      this.#kg = Math.round(monthsToKg(months) * 2) / 2;
    }
    this.#render();
  }

  /** @param {number} direction */
  #scrollCards(direction) {
    const scroller = this.querySelector('[data-cards-scroller]');
    if (!(scroller instanceof HTMLElement)) return;
    const card = scroller.querySelector('.size-guide__card');
    const amount = card instanceof HTMLElement ? card.offsetWidth + 10 : 200;
    scroller.scrollBy({ left: direction * amount, behavior: 'smooth' });
  }

  #updateCardsProgress() {
    const scroller = this.querySelector('[data-cards-scroller]');
    const fill = this.querySelector('[data-cards-progress]');
    if (!(scroller instanceof HTMLElement) || !(fill instanceof HTMLElement)) return;
    const max = scroller.scrollWidth - scroller.clientWidth;
    const ratio = max > 0 ? scroller.scrollLeft / max : 0;
    fill.style.width = `${Math.max(20, ratio * 100)}%`;
  }

  #activeProducts() {
    return this.#products.filter((p) => this.#kg >= p.min && this.#kg <= p.max);
  }

  /**
   * @param {number} months
   */
  #formatAgeLabel(months) {
    const m = Math.round(months);
    if (m <= 0) return this.#t('newborn');
    if (m < 24) return this.#t('age_months', { count: m });
    const years = Math.round((m / 12) * 10) / 10;
    if (Number.isInteger(years) || years === Math.floor(years)) {
      return this.#t('age_years', { count: Math.round(years) });
    }
    return this.#t('age_years', { count: years });
  }

  /**
   * @param {number} months
   */
  #formatAgeRange(months) {
    const m = Math.round(months);
    if (m <= 1) return this.#t('newborn_paren');
    if (m < 24) {
      const lo = Math.max(0, m - 1);
      const hi = m + 1;
      return this.#t('age_range_months', { lo, hi });
    }
    const years = Math.round(m / 12);
    return this.#t('age_approx_years', { count: years });
  }

  #render() {
    const months = kgToMonths(this.#kg);
    const primary = this.querySelector('[data-primary-value]');
    const secondary = this.querySelector('[data-secondary-value]');

    if (primary && secondary) {
      if (this.#mode === 'weight') {
        primary.textContent = this.#t('kg_value', { value: formatKg(this.#kg) });
        secondary.textContent = this.#formatAgeRange(months);
      } else {
        primary.textContent = this.#formatAgeLabel(months);
        secondary.textContent = this.#t('kg_paren', { value: formatKg(this.#kg) });
      }
    }

    this.querySelectorAll('[data-mode]').forEach((btn) => {
      const active = btn.getAttribute('data-mode') === this.#mode;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    const percent =
      this.#mode === 'weight' ? weightPercent(this.#kg) : monthsToAxis(kgToMonths(this.#kg));
    const fill = this.querySelector('[data-slider-fill]');
    const thumb = this.querySelector('[data-slider-thumb]');
    const track = this.querySelector('[data-slider-track]');
    if (fill instanceof HTMLElement) fill.style.width = `${percent}%`;
    if (thumb instanceof HTMLElement) thumb.style.left = `${percent}%`;
    if (track instanceof HTMLElement) {
      track.setAttribute('aria-valuenow', String(this.#mode === 'weight' ? this.#kg : Math.round(months)));
      track.setAttribute(
        'aria-valuetext',
        this.#mode === 'weight'
          ? this.#t('aria_valuetext_kg', { value: formatKg(this.#kg) })
          : this.#formatAgeLabel(months)
      );
      track.setAttribute(
        'aria-label',
        this.#mode === 'weight' ? this.#t('aria_child_weight') : this.#t('aria_child_age')
      );
    }

    this.#renderSliderLabels();

    // Chart indicator stays on the weight axis so bars stay aligned.
    const chartPercent = weightPercent(this.#kg);
    const indicator = this.querySelector('[data-chart-indicator]');
    if (indicator instanceof HTMLElement) {
      const meta = this.querySelector('.size-guide__chart-meta');
      const metaW = meta instanceof HTMLElement ? meta.offsetWidth : 140;
      indicator.style.left = `calc(${metaW}px + (100% - ${metaW}px) * ${chartPercent / 100})`;
    }

    this.querySelectorAll('[data-size-id]').forEach((row) => {
      const id = row.getAttribute('data-size-id');
      const product = this.#products.find((p) => p.id === id);
      if (!product) return;
      const active = this.#kg >= product.min && this.#kg <= product.max;
      row.classList.toggle('is-active', active);
    });

    this.#renderCards();
  }

  #renderSliderLabels() {
    const labels = this.querySelector('[data-slider-labels]');
    if (!(labels instanceof HTMLElement)) return;

    if (this.#mode === 'weight') {
      labels.classList.remove('size-guide__slider-labels--age');
      labels.innerHTML = `
        <span data-slider-min>${escapeHtml(this.#t('kg_value', { value: 3 }))}</span>
        <span data-slider-mid>${escapeHtml(this.#t('kg_value', { value: 9 }))}</span>
        <span data-slider-max>${escapeHtml(this.#t('kg_value', { value: 20 }))}</span>
      `;
      return;
    }

    labels.classList.add('size-guide__slider-labels--age');
    labels.innerHTML = AGE_MARKERS.map((marker) => {
      const label = this.#t(marker.labelKey);
      return `<span class="size-guide__slider-age-mark" style="left: ${marker.axis}%">${escapeHtml(label)}</span>`;
    }).join('');
    requestAnimationFrame(() => this.#cullOverlappingAgeMarks(labels));
  }

  /**
   * Hide middle age labels that collide with neighbors (e.g. "födelse" + "2 mån").
   * Always keeps the first and last marks.
   * @param {HTMLElement} container
   */
  #cullOverlappingAgeMarks(container) {
    const marks = [...container.querySelectorAll('.size-guide__slider-age-mark')];
    if (marks.length < 3) return;

    const gap = 8;
    for (const mark of marks) {
      mark.style.visibility = '';
    }

    const last = marks[marks.length - 1];
    const lastLeft = last.getBoundingClientRect().left;
    let prevRight = marks[0].getBoundingClientRect().right;

    for (let i = 1; i < marks.length - 1; i++) {
      const mark = marks[i];
      const rect = mark.getBoundingClientRect();
      if (rect.left < prevRight + gap || rect.right + gap > lastLeft) {
        mark.style.visibility = 'hidden';
        continue;
      }
      prevRight = rect.right;
    }
  }

  #renderCards() {
    const scroller = this.querySelector('[data-cards-scroller]');
    if (!(scroller instanceof HTMLElement)) return;

    const active = this.#activeProducts();
    const cta = this.#t('card_cta');
    scroller.innerHTML = active
      .map((p) => {
        const meta = p.pack
          ? this.#t('card_meta', {
              min: p.min,
              max: p.max,
              pack: p.pack,
              pack_label: p.packLabel,
            })
          : this.#t('card_meta_weight_only', { min: p.min, max: p.max });
        const tone = p.type === 'pullup' ? 'pullup' : 'diaper';
        const title = escapeHtml(p.cardTitle || p.name);
        const image = escapeHtml(p.image);
        const url = escapeHtml(p.url);
        return `
          <article class="size-guide__card size-guide__card--${tone}">
            <div class="size-guide__card-media">
              <img src="${image}" alt="" width="66" height="78" loading="lazy">
            </div>
            <div class="size-guide__card-body">
              <div class="size-guide__card-copy">
                <p class="size-guide__card-title">${title}</p>
                <p class="size-guide__card-meta">${escapeHtml(meta)}</p>
              </div>
              <a class="size-guide__card-cta" href="${url}">${escapeHtml(cta)}</a>
            </div>
          </article>
        `;
      })
      .join('');

    requestAnimationFrame(() => this.#updateCardsProgress());
  }
}

if (!customElements.get('size-guide-app')) {
  customElements.define('size-guide-app', SizeGuideApp);
}

/**
 * Opens the size guide dialog from external triggers (e.g. legacy SizeHeader link).
 * @param {Event} [event]
 */
export function openSizeGuideDialog(event) {
  event?.preventDefault?.();
  const host = document.querySelector('dialog-component.size-guide-block');
  if (host instanceof DialogComponent) {
    host.showDialog();
  }
}

document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const trigger = target.closest('.SizeHeader__guide, [data-open-size-guide]');
    if (!trigger) return;
    openSizeGuideDialog(event);
  },
  true
);
